import React, { useEffect, useMemo, useState } from 'react';
import {
    App as AntApp,
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Layout,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { http, tokenStore } from '../api';
import type { Customer, License, LicenseActivation, Product } from '../types';

type LoginForm = { email: string; password: string };
type ProductForm = Omit<Product, 'id'>;
type CustomerForm = Omit<Customer, 'id'>;
type LicenseForm = {
    product_id: number;
    customer_id: number;
    status: string;
    max_activations: number;
    starts_at?: Dayjs | null;
    expires_at?: Dayjs | null;
    notes?: string;
};

const STATUS_COLORS: Record<string, string> = {
    active: 'green',
    pending: 'gold',
    suspended: 'orange',
    expired: 'red',
    revoked: 'volcano',
};

export function App() {
    const { message } = AntApp.useApp();
    const [loggedIn, setLoggedIn] = useState<boolean>(Boolean(tokenStore.get()));
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [licenses, setLicenses] = useState<License[]>([]);
    const [plainLicenseKey, setPlainLicenseKey] = useState<string | null>(null);
    const [selectedLicenseActivations, setSelectedLicenseActivations] = useState<License | null>(null);
    const [isEditLicenseModalOpen, setIsEditLicenseModalOpen] = useState(false);
    const [editingLicense, setEditingLicense] = useState<License | null>(null);

    const [loginForm] = Form.useForm<LoginForm>();
    const [productForm] = Form.useForm<ProductForm>();
    const [customerForm] = Form.useForm<CustomerForm>();
    const [licenseForm] = Form.useForm<LicenseForm>();
    const [editLicenseForm] = Form.useForm<LicenseForm>();

    const licenseColumns: ColumnsType<License> = useMemo(() => ([
        { title: 'ID', dataIndex: 'id', width: 70 },
        {
            title: 'Статус',
            dataIndex: 'status',
            render: (status: string) => <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>,
        },
        { title: 'Ключ', dataIndex: 'license_key_preview' },
        {
            title: 'Продукт',
            render: (_, record) => `${record.product?.name} (${record.product?.code})`,
        },
        { title: 'Клиент', render: (_, record) => record.customer?.company_name ?? '-' },
        { title: 'До', render: (_, record) => record.expires_at ? dayjs(record.expires_at).format('YYYY-MM-DD') : '-' },
        { title: 'Актив.', render: (_, record) => record.activations?.filter((a) => !a.revoked_at).length ?? 0 },
        {
            title: 'Действия',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => setSelectedLicenseActivations(record)}>
                        Активации
                    </Button>
                    <Button size="small" onClick={() => openEditLicenseModal(record)}>
                        Редактировать
                    </Button>
                    <Button size="small" danger={record.status !== 'revoked'} onClick={() => handleLicenseAction(record.id, record.status === 'revoked' ? 'restore' : 'revoke')}>
                        {record.status === 'revoked' ? 'Восстановить' : 'Отозвать'}
                    </Button>
                </Space>
            ),
        },
    ]), [licenses]);

    const activationColumns: ColumnsType<LicenseActivation> = useMemo(() => ([
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: 'App', dataIndex: 'app_code', width: 80 },
        { title: 'IP', dataIndex: 'ip_address', render: (value?: string | null) => value ?? '-' },
        { title: 'Источник', render: (_, activation) => activation.meta?.domain ?? activation.source_host ?? '-' },
        {
            title: 'Активирована',
            render: (_, activation) => (activation.activated_at ?? activation.created_at)
                ? dayjs(activation.activated_at ?? activation.created_at).format('YYYY-MM-DD HH:mm:ss')
                : '-',
        },
        {
            title: 'Последняя проверка',
            render: (_, activation) => activation.last_seen_at ? dayjs(activation.last_seen_at).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: 'Статус',
            render: (_, activation) => activation.revoked_at ? <Tag color="red">revoked</Tag> : <Tag color="green">active</Tag>,
        },
    ]), []);

    useEffect(() => {
        if (loggedIn) {
            void bootstrap();
        }
    }, [loggedIn]);

    const bootstrap = async () => {
        setLoading(true);
        try {
            const [productsRes, customersRes, licensesRes] = await Promise.all([
                http.get('/admin/products'),
                http.get('/admin/customers'),
                http.get('/admin/licenses'),
            ]);

            setProducts(productsRes.data.data);
            setCustomers(customersRes.data.data);
            setLicenses(licensesRes.data.data);
        } catch (error) {
            tokenStore.clear();
            setLoggedIn(false);
            message.error('Сессия недействительна. Выполните вход.');
        } finally {
            setLoading(false);
        }
    };

    const submitLogin = async () => {
        const values = await loginForm.validateFields();
        setLoading(true);
        try {
            const response = await http.post('/admin/login', values);
            tokenStore.set(response.data.token);
            setLoggedIn(true);
            message.success('Вход выполнен');
        } catch {
            message.error('Неверный email или пароль');
        } finally {
            setLoading(false);
        }
    };

    const addProduct = async () => {
        const values = await productForm.validateFields();
        await http.post('/admin/products', values);
        productForm.resetFields();
        message.success('Продукт создан');
        await bootstrap();
    };

    const addCustomer = async () => {
        const values = await customerForm.validateFields();
        await http.post('/admin/customers', values);
        customerForm.resetFields();
        message.success('Клиент добавлен');
        await bootstrap();
    };

    const addLicense = async () => {
        const values = await licenseForm.validateFields();
        const payload = {
            ...values,
            starts_at: values.starts_at ? values.starts_at.toISOString() : null,
            expires_at: values.expires_at ? values.expires_at.toISOString() : null,
        };
        const response = await http.post('/admin/licenses', payload);
        licenseForm.resetFields();
        setPlainLicenseKey(response.data.license_key);
        message.success('Лицензия создана');
        await bootstrap();
    };

    const handleLicenseAction = async (licenseId: number, action: 'revoke' | 'restore') => {
        await http.post(`/admin/licenses/${licenseId}/${action}`);
        message.success(action === 'revoke' ? 'Лицензия отозвана' : 'Лицензия восстановлена');
        await bootstrap();
    };

    const openEditLicenseModal = (license: License) => {
        setEditingLicense(license);
        editLicenseForm.setFieldsValue({
            product_id: license.product.id,
            customer_id: license.customer.id,
            status: license.status,
            max_activations: license.max_activations,
            starts_at: license.starts_at ? dayjs(license.starts_at) : null,
            expires_at: license.expires_at ? dayjs(license.expires_at) : null,
        });
        setIsEditLicenseModalOpen(true);
    };

    const submitEditLicense = async () => {
        if (!editingLicense) {
            return;
        }

        const values = await editLicenseForm.validateFields();
        const payload = {
            ...values,
            starts_at: values.starts_at ? values.starts_at.toISOString() : null,
            expires_at: values.expires_at ? values.expires_at.toISOString() : null,
        };

        await http.put(`/admin/licenses/${editingLicense.id}`, payload);
        message.success('Лицензия обновлена');
        setIsEditLicenseModalOpen(false);
        setEditingLicense(null);
        await bootstrap();
    };

    const logout = async () => {
        try {
            await http.post('/admin/logout');
        } catch (_error) {
            // no-op
        }
        tokenStore.clear();
        setLoggedIn(false);
        message.info('Вы вышли из панели');
    };

    if (!loggedIn) {
        return (
            <Layout style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
                <Card title="Licensing Admin" style={{ width: 420 }}>
                    <Form form={loginForm} layout="vertical">
                        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                            <Input placeholder="admin@elcrm.ru" />
                        </Form.Item>
                        <Form.Item name="password" label="Пароль" rules={[{ required: true }]}>
                            <Input.Password placeholder="Введите пароль" />
                        </Form.Item>
                        <Button type="primary" block loading={loading} onClick={submitLogin}>
                            Войти
                        </Button>
                    </Form>
                </Card>
            </Layout>
        );
    }

    return (
        <Layout style={{ minHeight: '100vh', padding: 20 }}>
            <Card>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                        Единый сервер лицензирования
                    </Typography.Title>
                    <Button onClick={logout}>Выйти</Button>
                </Space>
            </Card>

            <Tabs
                style={{ marginTop: 16 }}
                items={[
                    {
                        key: 'licenses',
                        label: 'Лицензии',
                        children: (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Card title="Выдать лицензию вручную">
                                    <Form form={licenseForm} layout="vertical">
                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Form.Item name="product_id" label="Продукт" rules={[{ required: true }]}>
                                                    <Select options={products.map((product) => ({ label: `${product.name} (${product.code})`, value: product.id }))} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="customer_id" label="Клиент" rules={[{ required: true }]}>
                                                    <Select options={customers.map((customer) => ({ label: customer.company_name, value: customer.id }))} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="status" label="Статус" initialValue="active" rules={[{ required: true }]}>
                                                    <Select
                                                        options={[
                                                            { value: 'active', label: 'active' },
                                                            { value: 'pending', label: 'pending' },
                                                            { value: 'suspended', label: 'suspended' },
                                                            { value: 'expired', label: 'expired' },
                                                            { value: 'revoked', label: 'revoked' },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Form.Item name="max_activations" label="Макс. активаций" initialValue={1} rules={[{ required: true }]}>
                                                    <InputNumber min={1} max={20} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="starts_at" label="Начало">
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="expires_at" label="Окончание">
                                                    <DatePicker style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Form.Item name="notes" label="Примечание">
                                            <Input.TextArea rows={2} />
                                        </Form.Item>
                                        <Button type="primary" onClick={addLicense}>
                                            Выдать лицензию
                                        </Button>
                                    </Form>
                                </Card>

                                <Card title="Реестр лицензий">
                                    <Table loading={loading} rowKey="id" columns={licenseColumns} dataSource={licenses} pagination={{ pageSize: 10 }} />
                                </Card>
                            </Space>
                        ),
                    },
                    {
                        key: 'products',
                        label: 'Продукты',
                        children: (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Card title="Добавить продукт">
                                    <Form form={productForm} layout="vertical">
                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Form.Item name="code" label="Код" rules={[{ required: true }]}>
                                                    <Input placeholder="crm / arm" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="name" label="Название" rules={[{ required: true }]}>
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="default_duration_days" label="Дней по умолчанию" initialValue={365} rules={[{ required: true }]}>
                                                    <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Form.Item name="default_max_activations" label="Лимит активаций" initialValue={1} rules={[{ required: true }]}>
                                                    <InputNumber min={1} max={20} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={16}>
                                                <Form.Item name="description" label="Описание">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Form.Item name="is_active" label="Активен" initialValue={true}>
                                            <Select options={[{ value: true, label: 'Да' }, { value: false, label: 'Нет' }]} />
                                        </Form.Item>
                                        <Button type="primary" onClick={addProduct}>Сохранить</Button>
                                    </Form>
                                </Card>
                                <Card title="Список продуктов">
                                    <Table
                                        rowKey="id"
                                        dataSource={products}
                                        columns={[
                                            { title: 'Код', dataIndex: 'code' },
                                            { title: 'Название', dataIndex: 'name' },
                                            { title: 'Срок (дн.)', dataIndex: 'default_duration_days' },
                                            { title: 'Макс. активаций', dataIndex: 'default_max_activations' },
                                            { title: 'Статус', render: (_, row) => <Tag color={row.is_active ? 'green' : 'red'}>{row.is_active ? 'active' : 'disabled'}</Tag> },
                                        ]}
                                        pagination={false}
                                    />
                                </Card>
                            </Space>
                        ),
                    },
                    {
                        key: 'customers',
                        label: 'Клиенты',
                        children: (
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <Card title="Добавить клиента">
                                    <Form form={customerForm} layout="vertical">
                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Form.Item name="company_name" label="Компания" rules={[{ required: true }]}>
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="contact_name" label="Контактное лицо">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="email" label="Email">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={12}>
                                            <Col span={8}>
                                                <Form.Item name="phone" label="Телефон">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item name="external_id" label="Внешний ID">
                                                    <Input />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Button type="primary" onClick={addCustomer}>Сохранить</Button>
                                    </Form>
                                </Card>
                                <Card title="Клиентская база">
                                    <Table
                                        rowKey="id"
                                        dataSource={customers}
                                        columns={[
                                            { title: 'Компания', dataIndex: 'company_name' },
                                            { title: 'Контакт', dataIndex: 'contact_name' },
                                            { title: 'Email', dataIndex: 'email' },
                                            { title: 'Телефон', dataIndex: 'phone' },
                                        ]}
                                    />
                                </Card>
                            </Space>
                        ),
                    },
                ]}
            />

            <Modal
                title="Лицензия выдана"
                open={Boolean(plainLicenseKey)}
                onCancel={() => setPlainLicenseKey(null)}
                footer={[
                    <Button key="close" onClick={() => setPlainLicenseKey(null)}>
                        Закрыть
                    </Button>,
                ]}
            >
                <Typography.Paragraph>
                    Сохраните ключ и передайте клиенту. Повторно в полном виде он не хранится.
                </Typography.Paragraph>
                <Typography.Title level={4} copyable>
                    {plainLicenseKey}
                </Typography.Title>
            </Modal>

            <Modal
                title={editingLicense ? `Редактирование лицензии #${editingLicense.id}` : 'Редактирование лицензии'}
                open={isEditLicenseModalOpen}
                onCancel={() => {
                    setIsEditLicenseModalOpen(false);
                    setEditingLicense(null);
                }}
                onOk={submitEditLicense}
                okText="Сохранить"
                cancelText="Отмена"
            >
                <Form form={editLicenseForm} layout="vertical">
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="product_id" label="Продукт" rules={[{ required: true }]}>
                                <Select options={products.map((product) => ({ label: `${product.name} (${product.code})`, value: product.id }))} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="customer_id" label="Клиент" rules={[{ required: true }]}>
                                <Select options={customers.map((customer) => ({ label: customer.company_name, value: customer.id }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="status" label="Статус" rules={[{ required: true }]}>
                                <Select
                                    options={[
                                        { value: 'active', label: 'active' },
                                        { value: 'pending', label: 'pending' },
                                        { value: 'suspended', label: 'suspended' },
                                        { value: 'expired', label: 'expired' },
                                        { value: 'revoked', label: 'revoked' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="max_activations" label="Макс. активаций" rules={[{ required: true }]}>
                                <InputNumber min={1} max={20} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="starts_at" label="Начало">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="expires_at" label="Окончание">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="notes" label="Примечание">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={selectedLicenseActivations ? `Активации лицензии #${selectedLicenseActivations.id}` : 'Активации лицензии'}
                open={Boolean(selectedLicenseActivations)}
                onCancel={() => setSelectedLicenseActivations(null)}
                footer={[
                    <Button key="close" onClick={() => setSelectedLicenseActivations(null)}>
                        Закрыть
                    </Button>,
                ]}
                width={1100}
            >
                {selectedLicenseActivations && (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Typography.Text>
                            Ключ: <b>{selectedLicenseActivations.license_key_preview}</b>
                        </Typography.Text>
                        <Table
                            rowKey="id"
                            pagination={false}
                            columns={activationColumns}
                            dataSource={selectedLicenseActivations.activations ?? []}
                            expandable={{
                                expandedRowRender: (activation) => (
                                    <Space direction="vertical" size={2}>
                                        <Typography.Text code>installation_uuid: {activation.installation_uuid}</Typography.Text>
                                        <Typography.Text code>fingerprint: {activation.device_fingerprint}</Typography.Text>
                                        <Typography.Text>App version: {activation.app_version ?? '-'}</Typography.Text>
                                        <Typography.Text>Origin: {activation.meta?.origin ?? '-'}</Typography.Text>
                                        <Typography.Text>Referer: {activation.meta?.referer ?? '-'}</Typography.Text>
                                        <Typography.Text>Forwarded for: {activation.meta?.forwarded_for ?? '-'}</Typography.Text>
                                        <Typography.Text>User-Agent: {activation.user_agent ?? '-'}</Typography.Text>
                                    </Space>
                                ),
                            }}
                        />
                    </Space>
                )}
            </Modal>
        </Layout>
    );
}
